import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import ModelLifeCycleService from "src/services/modelLifeCycleService";
import CommonService from "src/services/commonService";
import { errorMedium, successMedium } from "src/utils/toastHelper";

enum ValidationJobStatus {
  PROCESSING = "Processing",
  COMPLETED = "Completed",
  ERROR = "Error",
}

export type GroundTruthPayload = {
  modelId: string;
};

export type GroundTruthOutput = {
  modelId: string;
  uploadId: string;
  fileName: string;
  s3FilePath: string;
  timestamp: string;
  isIndexedToOpenSearch: boolean;
};

export type GroundTruthFileResponse = {
  modelId: string;
  fileName: string;
  totalCount: number;
  newCount: number;
  existingCount: number;
  inValidCount: number;
  timestamp: string;
};

export type HistoricalValidationPayload = {
  modelId: string;
  uploadId: string;
};

export type Accuracy = {
  field: string;
  accuracy: number;
};

export interface UploadNewRevisionPayload {
  modelId: string;
  file: File;
  groundTruthUploadFileValue: GroundTruthUploadFileValue;
}

export interface PreProcessUploadNewRevisionPayload {
  modelId: string;
  file: File;
  groundTruthUploadFileValue: GroundTruthUploadFileValue;
}

export interface ValidatePayload {
  modelId: string;
  uploadId: string;
}

export type DownloadPayload = {
  jobId: string;
  s3URL: string;
  fileName: string;
};

export interface GetGroundTruthsCSVPayload {
  modelId: string;
}

export type GroundTruthUploadFileValue =
  | "source id and text only"
  | "confirmed answer"
  | "overwrite confirmed answer"
  | "llm answer";
export type GroundTruthUploadFileLabel =
  | "Source id and source text only"
  | "With reviewed field inputs"
  | "Overwrite confirmed answer"
  | "With pre labeled LLM Answer";

export type GroundTruthUploadFileOption = {
  id: string;
  value: GroundTruthUploadFileValue;
  label: GroundTruthUploadFileLabel;
};

export type ValidateOutput = {
  id: string;
  modelId: string;
  modelName: string;
  uploadId: string;
  ownerAlias: string;
  inputFileName: string;
  s3InputFilePath: string;
  s3GroundTruthFilePath: string | undefined;
  s3ExtractedResultFilePath: string | undefined;
  totalGroundTruthsNumber: number | undefined;
  totalReviewedNumber: number | undefined;
  newCount: number | undefined;
  existingCount: number | undefined;
  linesProcessed: number;
  linesFailed: number;
  accuracy: Accuracy[];
  timestamp: string;
  status: ValidationJobStatus;
};

export type ValidationWorkflowState = {
  file: File | null;
  currentGroundTruthError: string;
  uploadNewRevisionOutput: GroundTruthOutput | null;
  preProcessUploadNewRevisionError: string;
  preProcessUploadNewRevisionOutput: GroundTruthFileResponse | null;
  uploadNewRevisionModalOpen: boolean;
  groundTruthOutputs: GroundTruthOutput[] | null;
  historicalValidations: ValidateOutput[] | null;
  selectedUploadId: string;
  selectedUploadFileValues: GroundTruthUploadFileValue;
};

export const getCurrentGroundTruth = createAsyncThunk<GroundTruthOutput[], GroundTruthPayload>(
  "getCurrentGroundTruth",
  async ({ modelId }) => {
    const { data } = await ModelLifeCycleService.getCurrentGroundTruth(modelId);
    return data;
  },
);

export const getHistoricalValidation = createAsyncThunk<
  ValidateOutput[],
  HistoricalValidationPayload
>("getHistoricalValidation", async ({ modelId, uploadId }) => {
  const { data } = await ModelLifeCycleService.getHistoricalValidation(modelId, uploadId);
  if (data.length === 0) {
    errorMedium("There is no historical data for the selected file, please validate first.");
  }
  return data;
});

export const preProcessUploadNewRevision = createAsyncThunk<
  GroundTruthFileResponse,
  PreProcessUploadNewRevisionPayload
>("preProcessUploadNewRevision", async (payload: PreProcessUploadNewRevisionPayload) => {
  const { data } = await ModelLifeCycleService.preProcessUploadNewRevision(payload);
  return data;
});

export const uploadNewRevision = createAsyncThunk<GroundTruthOutput, UploadNewRevisionPayload>(
  "uploadNewRevision",
  async (payload: UploadNewRevisionPayload) => {
    const { data } = await ModelLifeCycleService.uploadNewRevision(payload);
    if (data && payload.groundTruthUploadFileValue !== "source id and text only") {
      successMedium(`File with reviewed field inputs has been submited for upload,
      you will receive email notification upon completion.`);
    }
    return data;
  },
);

export const validate = createAsyncThunk<ValidateOutput, ValidatePayload>(
  "validate",
  async (payload: ValidatePayload) => {
    const { data } = await ModelLifeCycleService.validate(payload);
    return data;
  },
);

export const download = createAsyncThunk(
  "download",
  CommonService.getModelLifeCyclePreSignedUrlAndDownload,
);

const initialState: ValidationWorkflowState = {
  file: null,
  currentGroundTruthError: "",
  uploadNewRevisionOutput: null,
  preProcessUploadNewRevisionError: "",
  preProcessUploadNewRevisionOutput: null,
  uploadNewRevisionModalOpen: false,
  groundTruthOutputs: null,
  historicalValidations: null,
  selectedUploadId: "",
  selectedUploadFileValues: "source id and text only",
};

const { reducer, actions } = createSlice({
  name: "validationWorkflow",
  initialState,
  reducers: {
    setSelectedUploadId: (state, action) => {
      state.selectedUploadId = action.payload;
    },
    setFile: (state, action) => {
      state.file = action.payload;
    },
    resetHistoricalValidation: (state) => {
      state.historicalValidations = null;
    },
    resetCurrentGroundTruthError: (state) => {
      state.currentGroundTruthError = "";
    },
    resetPreProcessUploadNewRevisionError: (state) => {
      state.preProcessUploadNewRevisionError = "";
    },
    resetUploadNewRevisionOutput: (state) => {
      state.uploadNewRevisionOutput = null;
    },
    resetGroundTruthOutput: (state) => {
      state.groundTruthOutputs = null;
      state.uploadNewRevisionOutput = null;
    },
    setSelectedUploadFileValues: (state, action) => {
      state.selectedUploadFileValues = action.payload;
    },
    setUploadNewRevisionModalOpen: (state, action) => {
      state.uploadNewRevisionModalOpen = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getCurrentGroundTruth.rejected, (state, action) => {
      state.groundTruthOutputs = null;
    });
    builder.addCase(
      getCurrentGroundTruth.fulfilled,
      (state, action: PayloadAction<GroundTruthOutput[]>) => {
        state.groundTruthOutputs = action.payload;
      },
    );
    builder.addCase(
      getHistoricalValidation.fulfilled,
      (state, action: PayloadAction<ValidateOutput[]>) => {
        state.historicalValidations = action.payload;
      },
    );
    builder.addCase(
      uploadNewRevision.fulfilled,
      (state, action: PayloadAction<GroundTruthOutput>) => {
        state.uploadNewRevisionOutput = action.payload;
      },
    );
    builder.addCase(preProcessUploadNewRevision.rejected, (state, action) => {
      state.preProcessUploadNewRevisionError = "Failed to upload!";
    });
    builder.addCase(
      preProcessUploadNewRevision.fulfilled,
      (state, action: PayloadAction<GroundTruthFileResponse>) => {
        state.preProcessUploadNewRevisionOutput = action.payload;
        state.uploadNewRevisionModalOpen = true;
      },
    );
  },
});

export const {
  setFile,
  setSelectedUploadId,
  resetCurrentGroundTruthError,
  resetPreProcessUploadNewRevisionError,
  resetUploadNewRevisionOutput,
  resetGroundTruthOutput,
  resetHistoricalValidation,
  setSelectedUploadFileValues,
  setUploadNewRevisionModalOpen,
} = actions;

export default reducer;
